import React, { useState, useEffect, useCallback } from 'react';
import { getInstitutionId } from '../../institutionFetch';
import { fetchOnboardingStatus, fetchInstitutionConfig, fetchDataHealth } from './onboardingApi';
import RunPanel from './RunPanel';
import PipelineRuns from './PipelineRuns';

const STATUS_STYLE = {
  complete: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'Complete' },
  missing: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', label: 'Missing' },
  manual: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400', label: 'Manual' },
};

const CATEGORY_LABELS = {
  config: 'Configuration',
  data: 'Catalog data',
  infra: 'Infrastructure (manual steps)',
};

const CATEGORY_ORDER = ['config', 'data', 'infra'];

const Card = ({ label, value, color = 'text-gray-900', sub }) => (
  <div className="bg-white rounded-lg border p-4">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.missing;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const SurfaceRow = ({ surface }) => {
  const { label, status, optional, count, detail } = surface;
  return (
    <div className="flex items-center gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0">
      <div className="w-28 flex-shrink-0"><StatusBadge status={status} /></div>
      <div className="flex-1">
        <span className="text-gray-800">{label}</span>
        {optional && <span className="ml-2 text-xs text-gray-400">(optional)</span>}
        {detail && <span className="ml-2 text-xs text-gray-400">— {detail}</span>}
      </div>
      {count !== null && count !== undefined && (
        <span className="text-gray-500 tabular-nums text-xs">{count}</span>
      )}
    </div>
  );
};

const BAND_STYLE = {
  good: { bg: 'bg-green-100', text: 'text-green-800', label: 'Good' },
  warn: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Needs review' },
  poor: { bg: 'bg-red-100', text: 'text-red-800', label: 'Poor' },
  unknown: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Unknown' },
};

const BandBadge = ({ band }) => {
  const s = BAND_STYLE[band] || BAND_STYLE.unknown;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

const DataHealth = ({ data }) => {
  if (!data || !data.found) return null;
  const { catalog, prerequisites, aliases, grade_data } = data.metrics;
  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Data health</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label="Courses" value={catalog.courses} sub={`${catalog.departments} departments`} />
        <Card
          label="Prereq coverage"
          value={prerequisites.parsed_pct === null ? '—' : `${prerequisites.parsed_pct}%`}
          sub={`${prerequisites.parsed}/${prerequisites.total} parsed`}
        />
        <Card
          label="Avg confidence"
          value={prerequisites.avg_confidence === null ? '—' : prerequisites.avg_confidence}
          sub={`${prerequisites.low_confidence} low-confidence`}
        />
        <Card
          label="Cross-listings"
          value={aliases.cross_listed}
          sub={`${aliases.total} aliases total`}
        />
      </div>
      <div className="mt-3 flex items-center gap-4 text-sm text-gray-600">
        <span className="flex items-center gap-2">
          Prerequisite quality: <BandBadge band={prerequisites.band} />
        </span>
        <span>
          Reviewed: {prerequisites.reviewed}/{prerequisites.total}
        </span>
        <span>
          Grade data: {grade_data.enabled
            ? `enabled (${grade_data.courses_with_grades} courses)`
            : 'not available'}
        </span>
      </div>
    </div>
  );
};

const ConfigView = ({ config }) => {
  if (!config || config.length === 0) {
    return <div className="text-sm text-gray-400">No institution_config rows.</div>;
  }
  return (
    <details className="bg-white rounded-lg border p-4">
      <summary className="text-sm font-semibold text-gray-700 cursor-pointer">
        institution_config ({config.length} keys)
      </summary>
      <div className="mt-3 space-y-3">
        {config.map((row) => (
          <div key={row.config_key} className="text-sm">
            <div className="font-mono text-xs font-semibold text-gray-700">{row.config_key}</div>
            <pre className="mt-1 text-xs bg-gray-50 rounded p-2 overflow-x-auto text-gray-600">
              {JSON.stringify(row.config_value, null, 2)}
            </pre>
          </div>
        ))}
      </div>
    </details>
  );
};

function Onboarding() {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState([]);
  const [dataHealth, setDataHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const institutionId = getInstitutionId();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, dh] = await Promise.all([
        fetchOnboardingStatus(institutionId),
        fetchInstitutionConfig(institutionId),
        fetchDataHealth(institutionId),
      ]);
      setStatus(s);
      setConfig(c);
      setDataHealth(dh);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-gray-500">Loading onboarding status…</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;
  if (!status || !status.found) {
    return (
      <div className="space-y-6">
        <div className="text-gray-500">No institution found (id={institutionId}).</div>
        <PipelineRuns />
      </div>
    );
  }

  const { slug, name, summary, surfaces } = status;
  const byCategory = surfaces.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Onboarding — {name}</h2>
          <div className="text-sm text-gray-500">
            slug: <span className="font-mono">{slug}</span> · institution_id: {status.institution_id}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Computed live from the database. Switch institution with the selector above.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label="Surfaces" value={summary.total} />
        <Card label="Complete" value={summary.complete} color="text-green-600" />
        <Card
          label="Required gaps"
          value={summary.required_missing}
          color={summary.required_missing ? 'text-red-600' : 'text-green-600'}
          sub={summary.required_missing ? 'needs seeding' : 'none — ready'}
        />
        <Card label="Manual steps" value={summary.manual} color="text-gray-600" />
      </div>

      {CATEGORY_ORDER.filter((cat) => byCategory[cat]).map((cat) => (
        <div key={cat} className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{CATEGORY_LABELS[cat]}</h3>
          <div>
            {byCategory[cat].map((s) => <SurfaceRow key={s.key} surface={s} />)}
          </div>
        </div>
      ))}

      <RunPanel slug={slug} />

      <PipelineRuns currentSlug={slug} />

      <DataHealth data={dataHealth} />

      <ConfigView config={config} />
    </div>
  );
}

export default Onboarding;
